'use client';

import { SalePageSaleStatus } from '@/api/types';
import { Tag } from '@/components';
import { Column, Element, Row } from '@vuddy/components';
import clsx from 'clsx';
import { useMemo } from 'react';
import * as styles from './fundingStatus.css';

interface FundingStatusProps {
  amount: number;
  achievementRate: number;
  saleStatus: SalePageSaleStatus;
  daysLeft?: number;
}

const formatCurrency = (amount: number) => {
  return amount.toLocaleString() + '원';
};

const getStatusLabel = (saleStatus: SalePageSaleStatus, daysLeft?: number) => {
  switch (saleStatus) {
    case 'PLANNED':
      return '오픈 전';
    case 'SOLD_OUT':
      return '품절';
    case 'STOPPED':
      return '판매 중단';
    case 'ENDED':
      return '종료';
    case 'ON_SALE':
      return `${daysLeft}일 남음`;
    default:
      return '';
  }
};

const getTagProps = (saleStatus: SalePageSaleStatus, daysLeft?: number) => {
  const label = getStatusLabel(saleStatus, daysLeft);

  // 판매중 외에는 모두 'gray' 컬러
  if (saleStatus !== 'ON_SALE') {
    return { label, color: 'gray' as const };
  }

  // 판매중일때는 3일 이내면 'red', 3일보다 이상이면 'blue'
  if (daysLeft !== undefined && daysLeft <= 3) {
    return { label, color: 'lightred' as const };
  }

  return { label, color: 'lightblue' as const };
};

export const FundingStatus = ({
  amount,
  achievementRate,
  saleStatus,
  daysLeft,
}: FundingStatusProps) => {
  const tagProps = getTagProps(saleStatus, daysLeft);
  const isAchieved = useMemo(() => achievementRate >= 100, [achievementRate]);

  return (
    <Column className={clsx(styles.container, isAchieved && styles.achieved)}>
      <Row justifyContent="space-between">
        <Element typography="captionBaseAccent">팬들이 모은 금액</Element>
        <Tag label={tagProps.label} variant={tagProps.color} size="medium" />
      </Row>
      <Element typography="titleBase" color="vuddyPrimary">
        {formatCurrency(amount)}
        {isAchieved && <>{' 🎉'}</>}
      </Element>

      {/* 펀딩 현황 섹션 */}
      <Column gap="gap_small">
        {/* 진행률 바와 달성률 */}
        <Row justifyContent="space-between" alignItems="center" gap="gap_small">
          <Element className={styles.progressContainer}>
            <Element
              className={styles.progressBarNew}
              style={{ width: `${Math.min(achievementRate, 100)}%` }}
            />
          </Element>
          <Element typography="bodyXsAccent" color="vuddyPrimary">
            {Math.round(achievementRate)}%
          </Element>
        </Row>

        {/* 달성률에 따른 메시지 */}
        {isAchieved && (
          <Row
            borderRadius="radius_small"
            backgroundColor="vuddyQuaternary"
            justifyContent="center"
            alignItems="center"
            paddingX="padding_xs"
            paddingY="padding_3xs">
            <Element typography="captionBaseAccent" color="vuddyPrimary">
              {saleStatus === 'ON_SALE'
                ? '펀딩 목표 달성! 소중한 응원에 감사드립니다!'
                : saleStatus === 'ENDED' || saleStatus === 'SOLD_OUT'
                  ? '상품 제작 및 발송이 확정 되었습니다'
                  : '펀딩 목표 달성! 소중한 응원에 감사드립니다!'}
            </Element>
          </Row>
        )}
      </Column>
    </Column>
  );
};
